import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { FilterType } from '@/models';
import { FilterState } from '@/types/filter';
import { Period } from '@/types/date';
import { isSamePeriod } from '@/utils/date';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getQuickSelectOptions, QuickSelectOption } from './getQuickSelectOptions';

interface UseDateRangeDrawerReturn {
  open: boolean;
  hasPendingChanges: boolean;
  formRef: React.RefObject<HTMLFormElement | null>;
  methods: ReturnType<typeof useForm<FilterState>>;
  quickSelectOptions: QuickSelectOption[];
  selectedOptionId: string | null;
  isDirty: boolean;
  hasValidDateRanges: boolean;
  toggle: () => void;
  handleClose: () => void;
  handleCancel: () => void;
  handleApplyClick: () => void;
  handleQuickSelect: (option: QuickSelectOption) => void;
  handleClearAll: () => void;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

export const useDateRangeDrawer = (): UseDateRangeDrawerReturn => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pendingState, setPendingState] = useState<Partial<FilterState> | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const { filterState, handleApplyFilter, defaultFiltersState } = useFiltersContext();

  const methods = useForm<FilterState>({
    defaultValues: defaultFiltersState,
  });

  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = methods;

  const quickSelectOptions = useMemo(() => getQuickSelectOptions(t), [t]);

  const formDateRange = watch(FilterType.DATE_RANGE) as Period | undefined;
  const formComparisonPeriod = watch(FilterType.COMPARISON_PERIOD) as Period | undefined;

  const isValidPeriod = (period: Period | undefined): boolean => {
    return !!period && !!period.beginn && !!period.end;
  };

  const hasValidDateRanges = useMemo(() => {
    return isValidPeriod(formDateRange) && isValidPeriod(formComparisonPeriod);
  }, [formDateRange, formComparisonPeriod]);

  const getSelectedOptionId = useCallback(
    (options: QuickSelectOption[]): string | null => {
      if (!formDateRange || !formComparisonPeriod) {
        return null;
      }

      const selectedOption = options.find(
        option =>
          isSamePeriod(option.dateRange, formDateRange) && isSamePeriod(option.comparisonPeriod, formComparisonPeriod),
      );
      return selectedOption?.id ?? null;
    },
    [formDateRange, formComparisonPeriod],
  );

  const selectedOptionId = getSelectedOptionId(quickSelectOptions);
  const hasPendingChanges = pendingState !== null;

  const handleQuickSelect = useCallback(
    (option: QuickSelectOption) => {
      setValue(FilterType.DATE_RANGE, option.dateRange, { shouldDirty: true });
      setValue(FilterType.COMPARISON_PERIOD, option.comparisonPeriod, { shouldDirty: true });
    },
    [setValue],
  );

  const handleClearAll = useCallback(() => {
    setValue(FilterType.DATE_RANGE, undefined, { shouldDirty: true });
    setValue(FilterType.COMPARISON_PERIOD, undefined, { shouldDirty: true });
    setPendingState(null);
  }, [setValue]);

  const toggle = useCallback(() => setOpen(currentOpen => !currentOpen), []);

  const handleClose = useCallback(() => {
    if (isDirty || hasPendingChanges) {
      setPendingState({
        [FilterType.DATE_RANGE]: formDateRange,
        [FilterType.COMPARISON_PERIOD]: formComparisonPeriod,
      });
    }
    setOpen(false);
  }, [isDirty, hasPendingChanges, formDateRange, formComparisonPeriod]);

  const handleCancel = useCallback(() => {
    setPendingState(null);
    setOpen(false);
  }, []);

  const handleApplyClick = useCallback(() => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  }, []);

  const onSubmit = useCallback(
    (state: FilterState) => {
      handleApplyFilter(state);
      setPendingState(null);
      setOpen(false);
    },
    [handleApplyFilter],
  );

  useEffect(() => {
    if (open) {
      if (pendingState) {
        reset({
          ...filterState,
          ...pendingState,
        });
        setValue(FilterType.DATE_RANGE, pendingState[FilterType.DATE_RANGE], { shouldDirty: true });
        setValue(FilterType.COMPARISON_PERIOD, pendingState[FilterType.COMPARISON_PERIOD], { shouldDirty: true });
      } else {
        reset(filterState);
      }
    }
  }, [open]);

  const handleFormSubmit = handleSubmit(onSubmit);

  return {
    open,
    hasPendingChanges,
    formRef,
    methods,
    quickSelectOptions,
    selectedOptionId,
    isDirty,
    hasValidDateRanges,
    toggle,
    handleClose,
    handleCancel,
    handleApplyClick,
    handleQuickSelect,
    handleClearAll,
    onSubmit: handleFormSubmit,
  };
};
